<template>
  <page-loader>
    <v-container>
      <v-row>
        <v-col>
          <filter-accordion
            :loading="isLoading"
            :query-count="$_queryHandler_numberOfQueries"
            :query-changed="$_queryHandler_hasQueryChanged"
            @clear-filters="$_queryHandler_resetFilters"
          >
            <template #search>
              <search-input v-model="query.searchTerm" hide-details />
            </template>
            <template #default>
              <v-row>
                <v-col sm="6">
                  <select-list
                    v-model="query.params.resources"
                    :items="resourceOptions"
                    :limit="2"
                    label="Resource"
                    clearable
                    multiple
                    hide-details
                  />
                </v-col>
                <v-col sm="6">
                  <select-list
                    v-model="query.params.actions"
                    :disabled="!resourcesSelected"
                    :items="actions"
                    :limit="2"
                    label="Actions"
                    clearable
                    multiple
                    hide-details
                  />
                </v-col>
              </v-row>
            </template>
            <template #content>
              <data-table
                :headers="tableHeaders"
                :items="filteredRoles"
                :route="{ name: 'roles.edit', params: ['slug'] }"
                disable-pagination
                hide-default-footer
              >
                <template #item="{ item }">
                  <td>{{ item.name }}</td>
                  <td>
                    <role-chip
                      :value="resourcesFractionForUser(item)"
                      :color="hasAllResources(item) ? 'error' : undefined"
                    />
                  </td>
                </template>
                <template #actions="{ item }">
                  <v-list-item :disabled="!isPermittedTo('delete')" @click="onClickDelete(item)">
                    <v-list-item-title>
                      <v-icon left small>delete</v-icon>
                      <span>Delete</span>
                    </v-list-item-title>
                  </v-list-item>
                </template>
              </data-table>
            </template>
          </filter-accordion>
        </v-col>
      </v-row>
    </v-container>
    <fab-create v-if="isPermittedTo('create')" type="Role" @click="showCreateRoleModal = true" />

    <generic-modal
      v-model="showCreateRoleModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      title="Create a new role"
      persistent
      @modal-confirm="onModalCreateConfirm"
      @modal-cancel="onModalCreateCancel"
    >
      <v-row>
        <v-col sm="6">
          <text-input v-model="newRole" label="Enter a name" />
        </v-col>
      </v-row>
    </generic-modal>

    <generic-modal
      v-model="showDeleteRoleModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      :title="deleteModalTitle"
      persistent
      @modal-confirm="onModalDeleteConfirm"
      @modal-cancel="onModalDeleteCancel"
    >
      <template #subtitle>
        <span>Are you sure you want to remove this role?</span>
      </template>
    </generic-modal>
  </page-loader>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'

import PageLoader from '@/components/page-loader'
import SearchInput from '@/components/forms/search-input'
import FabCreate from '@/components/fab-create'
import GenericModal from '@/components/modals/generic-modal'
import TextInput from '@/components/forms/text-input'
import DataTable from '@/components/data-table'
import FilterAccordion from '@/components/filter-accordion'
import SelectList from '@/components/forms/select-list'
import RoleChip from '@/components/chips/role'

import PermissionsHandler from '@/mixins/permissions-handler'
import QueryParamsHandler from '@/mixins/query-params-handler'

import { users, roles, resources } from '@/services/offers'

const resetCurrentUser = () => ({
  name: null,
  email: null,
  console_roles: [],
})

export default {
  name: 'RolesManagement',
  components: {
    PageLoader,
    SearchInput,
    FabCreate,
    GenericModal,
    TextInput,
    DataTable,
    FilterAccordion,
    SelectList,
    RoleChip,
  },
  mixins: [PermissionsHandler, QueryParamsHandler],
  data: () => ({
    currentUser: resetCurrentUser(),
    isLoading: false,
    newRole: '',
    console_roles: [],
    resources: [],
    roleToDelete: {},
    searchKey: 'name',
    showCreateRoleModal: false,
    showDeleteRoleModal: false,
    tableHeaders: [{ text: 'Role', value: 'role' }, { text: 'Resources', value: 'resources' }, { value: 'actions' }],
    queryPagination: false,
    querySearch: false,
    queriesDefined: ['actions', 'resources'],
    queryTypes: { actions: 'array', resources: 'array' },
    actions: [
      { key: 'Create', value: 'create' },
      { key: 'Read', value: 'read' },
      { key: 'Update', value: 'update' },
      { key: 'Delete', value: 'delete' },
    ],
  }),
  computed: {
    ...mapGetters('general', ['hasPageErrors', 'isPageLoading']),
    ...mapGetters('options', ['getItemsForOption', 'hasOptions']),
    hasErrors() {
      return !this.hasOptions || this.hasPageErrors
    },
    resourceOptions() {
      return this.resources.map(({ name: key, slug: value }) => ({
        key,
        value,
      }))
    },
    deleteModalTitle() {
      return `Delete '${this.roleToDelete.name}' role`
    },
    filteredRoles() {
      if (!this.resourcesSelected) return this.console_roles

      const { actions, resources } = this.query.params

      return this.console_roles.filter(role => {
        return resources.every(slug => {
          return role.resources.find(resource => {
            const resourceExists = slug === resource.slug
            const canPerformActions =
              actions && actions.length
                ? this.resourceHasEveryAction(resource, actions)
                : this.resourceHasSomeActions(resource)
            return resourceExists && canPerformActions
          })
        })
      })
    },
    resourcesSelected() {
      return this.query.params.resources && this.query.params.resources.length
    },
  },
  watch: {
    'query.params.resources'(val) {
      if (!val?.length) this.$set(this.query.params, 'actions', [])
    },
  },
  created() {
    this.getUserRoles()
    this.getResourcesOptions()
    this.setPageLoading(false)
  },
  methods: {
    ...mapMutations('general', ['setPageLoading', 'setPageErrors']),
    async getUserRoles() {
      this.isLoading = true

      try {
        this.console_roles = await users.getUserRoles()
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve user roles',
        })
      }

      this.isLoading = false
    },
    async getResourcesOptions() {
      this.isLoading = true

      try {
        this.resources = await resources.getResources()
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve resource options',
        })
      }
      this.isLoading = false
    },
    onClickDelete(role) {
      this.roleToDelete = role
      this.showDeleteRoleModal = true
    },
    async onModalDeleteConfirm() {
      try {
        await roles.deleteRole(this.roleToDelete.slug)
        await this.getUserRoles()
        this.$notify({ type: 'success', text: 'Successfully removed role' })
      } catch (error) {
        this.$notify({ type: 'error', text: 'Unable to delete role' })
      }
      this.showDeleteRoleModal = false
      this.roleToDelete = {}
    },
    onModalDeleteCancel() {
      this.showDeleteRoleModal = false
      this.roleToDelete = {}
    },
    /**
     * Create a new role entry and if sucessful, retrieve the
     * slug and redirect to the edit page for the new role.
     */
    async onModalCreateConfirm() {
      try {
        const newRoleResponse = await roles.createRole({ name: this.newRole })
        this.$router.push({ name: 'roles.edit', params: { slug: newRoleResponse.slug } })
        this.$notify({ type: 'success', text: 'Successfully created new role' })
      } catch (error) {
        this.$notify({ type: 'error', text: 'Unable to create new role' })
        this.newRole = ''
        this.showCreateRoleModal = false
      }
    },
    onModalCreateCancel() {
      this.newRole = ''
      this.showCreateRoleModal = false
    },
    resourceHasSomeActions(resource, actions = ['create', 'read', 'update', 'delete']) {
      return actions.some(action => resource[action])
    },
    resourceHasEveryAction(resource, actions = ['create', 'read', 'update', 'delete']) {
      return actions.every(action => resource[action])
    },
    resourcesForUser(user) {
      return user.resources.reduce((count, resource) => {
        const canPerformAction = this.resourceHasSomeActions(resource)
        return count + (canPerformAction ? 1 : 0)
      }, 0)
    },
    totalResources() {
      return this.resources.length
    },
    resourcesFractionForUser(user) {
      const resourcesForUser = this.resourcesForUser(user)
      const totalResources = this.totalResources()
      return [resourcesForUser, totalResources].join('/')
    },
    hasAllResources(user) {
      return this.resourcesForUser(user) === this.totalResources()
    },
  },
}
</script>
